import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { type SxProps, type Theme } from "@mui/material";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useHistory } from "react-router-dom";

import { useCanBookLimitedAccessShifts } from "../../../ShiftDiscovery/LimitedAccess/hooks";
import { useFetchSubstage } from "../../hooks/useFetchSubstage";
import { CLIPBOARD_SCORE_FULL_PATH } from "../../utils/paths";
import { EnrolledBanner } from "./EnrolledBanner";
import { LimitedBanner } from "./LimitedBanner";

interface ClipboardScoreBannerProps {
  score?: number | undefined;
  showEmphasizedScore?: boolean;
  disableEnrolledBanner?: boolean;
  sx?: SxProps<Theme>;
}

export function ClipboardScoreBanner(props: ClipboardScoreBannerProps) {
  const {
    score: scoreFromProps,
    showEmphasizedScore = false,
    disableEnrolledBanner = false,
    sx,
  } = props;

  const history = useHistory();
  const { userId, stage } = useDefinedWorker();
  const { data: substage } = useFetchSubstage();
  const canBookLimitedAccessShifts = useCanBookLimitedAccessShifts();

  const { data: clipboardScoreData } = useGetClipboardScoreProfile(userId, {
    enabled: isNil(scoreFromProps) && isDefined(userId),
  });

  const showEnrolledBanner = !disableEnrolledBanner;

  const scoreToUse = scoreFromProps ?? clipboardScoreData?.clipboardScore;
  const onCtaClick = () => {
    history.push(CLIPBOARD_SCORE_FULL_PATH);
  };

  const workerType = clipboardScoreData?.clipboardScoreRank?.qualification ?? "Workers";
  const { data } = useGetQualificationDisplayLabels([workerType]);
  const qualificationLabel = data[0] ?? workerType;

  const { data: qualificationDisplayLabels } = useGetQualificationDisplayLabels(
    substage?.qualifications ?? []
  );

  if (isNil(scoreToUse)) {
    return null;
  }

  if (stage === WorkerStage.RESTRICTED && !canBookLimitedAccessShifts) {
    return null;
  }

  if (isDefined(substage) && substage.name === "LIMITED") {
    return (
      <LimitedBanner
        sx={sx}
        score={scoreToUse}
        showEmphasizedScore={showEmphasizedScore ?? false}
        thresholdScore={substage?.accessMetadata?.scoreThreshold ?? 0}
        basketSize={substage?.accessMetadata?.basketSize ?? 0}
        leadTimeInHours={substage?.accessMetadata?.leadTimeThresholdInHrs}
        qualificationDisplayLabelsLimited={qualificationDisplayLabels}
        onHelpClick={onCtaClick}
      />
    );
  }

  const showAsEnrolled =
    (stage === WorkerStage.ENROLLED && scoreToUse > 0) ||
    (stage === WorkerStage.RESTRICTED && canBookLimitedAccessShifts);

  if (showEnrolledBanner && showAsEnrolled) {
    return (
      <EnrolledBanner
        score={scoreToUse}
        rank={clipboardScoreData?.clipboardScoreRank?.rank}
        outOf={clipboardScoreData?.clipboardScoreRank?.outOf}
        qualification={qualificationLabel}
        onCtaClick={onCtaClick}
      />
    );
  }

  return null;
}
